import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight, FaBars } from 'react-icons/fa';
import { Badge } from '../Badge';
import { cn } from '../../utils/cn';

export interface SidebarItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  path?: string;
  href?: string;
  onClick?: () => void;
  badge?: string | number;
  children?: SidebarItem[];
}

export interface SidebarProps {
  items: SidebarItem[];
  activeItem?: string;
  onItemClick?: (item: SidebarItem) => void;
  isCollapsed?: boolean;
  isMobileOpen?: boolean;
  onToggle?: () => void;
  onMobileToggle?: () => void;
  className?: string;
}

export const Sidebar: React.FC<SidebarProps> = ({
  items,
  activeItem,
  onItemClick,
  isCollapsed = false,
  isMobileOpen = false,
  onToggle,
  onMobileToggle,
  className,
}) => {
  const [openSubmenus, setOpenSubmenus] = useState<Set<string>>(new Set());
  const navigate = useNavigate();
  const location = useLocation();

  const toggleSubmenu = (key: string) => {
    const newOpenSubmenus = new Set(openSubmenus);
    if (newOpenSubmenus.has(key)) {
      newOpenSubmenus.delete(key);
    } else {
      newOpenSubmenus.add(key);
    }
    setOpenSubmenus(newOpenSubmenus);
  };

  const handleItemClick = (item: SidebarItem) => {
    if (item.children && item.children.length > 0) {
      toggleSubmenu(item.key);
    } else {
      const path = item.href || item.path;
      if (path) {
        navigate(path);
      }
      onItemClick?.(item);
    }
  };

  const isItemActive = (item: SidebarItem): boolean => {
    if (activeItem) {
      return activeItem === item.key;
    }
    const path = item.href || item.path;
    return path ? location.pathname === path || location.pathname.startsWith(path + '/') : false;
  };

  const renderItem = (item: SidebarItem, level: number = 0) => {
    const isActive = isItemActive(item);
    const hasChildren = item.children && item.children.length > 0;
    const isSubmenuOpen = openSubmenus.has(item.key);
    const isSubmenuItem = level > 0;

    return (
      <div key={item.key}>
        <button
          onClick={() => handleItemClick(item)}
          className={cn(
            'flex items-center px-3 py-2 text-sm transition-colors',
            'focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-500)] focus:ring-offset-2',
            isSubmenuItem ? 'ml-4 w-[calc(100%-2rem)]' : 'w-full',
            isSubmenuItem ? 'rounded-md' : 'rounded-lg',
            isActive
              ? 'bg-[var(--color-primary-100)] text-[var(--color-primary-700)]'
              : 'text-[var(--color-text-primary)] hover:bg-[var(--color-hover-background)]',
            isCollapsed && 'justify-center px-2',
            !isCollapsed && hasChildren && 'justify-between'
          )}
          style={
            isActive
              ? {
                  backgroundColor: 'var(--color-primary-100)',
                  color: 'var(--color-primary-700)',
                }
              : undefined
          }
        >
          <div className="flex items-center space-x-3">
            <span className="flex-shrink-0">{item.icon}</span>
            {!isCollapsed && (
              <>
                <span className="font-semibold leading-6">{item.label}</span>
                {item.badge && (
                  <Badge variant="default" size="sm" className="ml-auto">
                    {item.badge}
                  </Badge>
                )}
              </>
            )}
          </div>
          {!isCollapsed && hasChildren && (
            <span
              className={cn(
                'transition-transform duration-200',
                isSubmenuOpen ? 'rotate-180' : 'rotate-0'
              )}
            >
              ▼
            </span>
          )}
        </button>

        {hasChildren && !isCollapsed && isSubmenuOpen && (
          <div className="mt-1">
            <div className="space-y-1">
              {item.children?.map(child => renderItem(child, level + 1))}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (isMobileOpen) {
    return (
      <nav
        className="fixed top-0 left-0 h-full w-64 z-50 transform transition-transform duration-300 ease-in-out translate-x-0"
        style={{
          backgroundColor: 'var(--color-background-paper)',
          borderRight: '1px solid var(--color-border-light)',
        }}
      >
        <div className="p-4">
          {onMobileToggle && (
            <button
              onClick={onMobileToggle}
              className="mb-4 p-2 rounded-lg hover:bg-[var(--color-hover-background)] transition-colors w-full flex items-center justify-between"
              aria-label="Close sidebar"
              type="button"
            >
              <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                Close Menu
              </span>
              <FaBars className="h-5 w-5" style={{ color: 'var(--color-text-primary)' }} />
            </button>
          )}
          <div className="space-y-1">{items.map(item => renderItem(item))}</div>
        </div>
      </nav>
    );
  }

  return (
    <nav
      className={cn(
        'border-r relative flex flex-col',
        isCollapsed ? 'w-16' : 'w-64',
        'hidden md:block',
        className
      )}
      style={{
        backgroundColor: 'var(--color-background-paper)',
        borderColor: 'var(--color-border-light)',
      }}
    >
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-1">{items.map(item => renderItem(item))}</div>
      </div>
      {onToggle && (
        <button
          onClick={onToggle}
          className={cn(
            'w-full py-2 px-3 border-t flex items-center justify-center transition-colors',
            'hover:bg-[var(--color-hover-background)]',
            isCollapsed ? 'justify-center' : 'justify-end'
          )}
          style={{
            borderColor: 'var(--color-border-light)',
            color: 'var(--color-text-primary)',
          }}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          type="button"
        >
          {isCollapsed ? (
            <FaChevronRight className="h-3 w-3" />
          ) : (
            <FaChevronLeft className="h-3 w-3" />
          )}
        </button>
      )}
    </nav>
  );
};


